import { TypedDocumentNode, gql } from '@apollo/client';
import { faHourglass, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useCallback, useMemo, useState } from 'react';
import { useHits } from 'react-instantsearch';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import {
  BlueprintRevealStatus,
  Rarity,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { Text16, Title3 } from '@sorare/core/src/atoms/typography';
import Bold from '@sorare/core/src/atoms/typography/Bold';
import { Empty } from '@sorare/core/src/components/cards/Empty';
import ConfirmDialog from '@sorare/core/src/components/form/ConfirmDialog';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import {
  ANY_SPORT_MARKET_INSTANT_BUY,
  ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE,
} from '@sorare/core/src/constants/routes';
import { useEventsContext } from '@sorare/core/src/contexts/events';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import idFromObject from '@sorare/core/src/gql/idFromObject';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';
import { joinFiltersWithOr } from '@sorare/core/src/lib/algolia';
import { glossary, inventoryMessages } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import FlexCard from '@sorare/marketplace/src/components/token/FlexCard';
import SearchBox from '@sorare/marketplace/src/search/SearchBox';
import SortCards from '@sorare/marketplace/src/searchCards/SortCards';

import { InventoryBlueprintTypeQuery } from 'layouts/inventory/InventoryRevealableCardsTypeLayout/__generated__/index.graphql';
import { useInventoryRevealableCardsTypeLayoutData } from 'layouts/inventory/InventoryRevealableCardsTypeLayout/useInventoryRevealableCardsTypeLayoutData';

import {
  RevealAllCards,
  RevealAllCardsVariables,
} from './__generated__/index.graphql';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  @media ${tabletAndAbove} {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  gap: var(--double-unit);
  padding: var(--double-unit) var(--quadruple-unit);
`;

const SectionSeparator = styled(Horizontal)`
  padding: var(--unit) var(--double-unit) var(--unit) var(--double-unit);
  background-color: rgba(255, 255, 255, 0.07);
`;
const Info = styled(Horizontal).attrs({ gap: 2 })`
  max-width: 500px;
  margin: 0 auto;
  background-color: rgba(255, 255, 255, 0.07);
  padding: var(--double-unit);
  border-radius: var(--double-unit);
`;

const ItemButton = styled(ButtonBase)`
  :hover {
    transform: scale(1.006);
    transition: all 0.2s ease-in-out;
  }
`;

const Actions = styled(Horizontal)`
  padding: var(--double-unit);
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  background: var(--c-neutral-100);
`;
const SearchBoxWrapper = styled(Horizontal)`
  flex: 1;
`;

type RevealableCard = NonNullable<
  InventoryBlueprintTypeQuery['currentUser']
>['blueprintCards']['nodes'][number];

const MUTATION_REVEAL_All_CARDS = gql`
  mutation RevealAllCards($input: revealBlueprintsInput!) {
    revealBlueprints(input: $input) {
      cards {
        slug
        blueprint {
          id
          revealStatus
        }
      }
    }
  }
` as TypedDocumentNode<RevealAllCards, RevealAllCardsVariables>;

const RevealableCardsGrid = ({
  cards,
  onSelect,
  showRevealed,
}: {
  cards: RevealableCard[];
  onSelect: (index: number) => void;
  showRevealed: () => void;
}) => {
  const { generateSportContextPath } = useSportContext();
  const { items, results } = useHits();
  const { formatMessage } = useIntl();

  // Do not update when blueprints changes to keep revealed cards visible
  const revealableCards = cards.filter(
    c => c.blueprint?.revealStatus === BlueprintRevealStatus.REVEALABLE
  );
  const notYetRevealableCards = cards.filter(
    c =>
      c.blueprint?.revealStatus === BlueprintRevealStatus.DELAYED ||
      c.blueprint?.revealStatus === BlueprintRevealStatus.NOT_YET_REVEALABLE
  );

  const matchingRevealableCards = useMemo(
    () =>
      items
        .map(hit => revealableCards.find(c => c.slug === hit.objectID))
        .filter(Boolean),
    [revealableCards, items]
  );

  const [mutate] = useMutation(MUTATION_REVEAL_All_CARDS);
  const [showConfirmRevealAll, setShowConfirmRevealAll] =
    useState<boolean>(false);
  const { track } = useEventsContext();

  const revealAll = async () => {
    const cardSlugs = cards
      .filter(
        c => c.blueprint?.revealStatus === BlueprintRevealStatus.REVEALABLE
      )
      .map(c => c.slug);
    await mutate({
      variables: {
        input: {
          cardSlugs,
        },
      },
    });

    track('Reveal All Early Access Cards', {
      cardSlugs,
    });

    showRevealed();
  };

  return (
    <>
      <Actions>
        <SearchBoxWrapper>
          <SearchBox
            withClearIcon
            autoFocus
            placeholder={formatMessage(inventoryMessages.searchPlaceholder)}
          />
        </SearchBoxWrapper>
        <SortCards
          indexes={[
            'Cards New',
            'Cards Highest Average Score',
            'Cards Highest Price',
            'Cards Lowest Price',
            'Cards Player Name',
          ]}
        />
        <Button
          color="gray"
          onClick={() => setShowConfirmRevealAll(true)}
          size="small"
        >
          <FormattedMessage {...inventoryMessages.revealAll} />
        </Button>
        <ConfirmDialog
          open={showConfirmRevealAll}
          onConfirm={revealAll}
          onClose={() => {
            setShowConfirmRevealAll(false);
          }}
          message={
            <Vertical>
              <Title3>
                {formatMessage(inventoryMessages.revealAllConfirmTitle)}
              </Title3>
              <Text16>
                {formatMessage(
                  {
                    id: 'Inventory.RevealableCards.revealAll.confirm.message',
                    defaultMessage:
                      'You are about to reveal all your preorder edition cards.{br} This action cannot be undone',
                  },
                  {
                    br: <br />,
                  }
                )}
              </Text16>
            </Vertical>
          }
          cancelCta={formatMessage(glossary.cancel)}
          cta={formatMessage({
            id: 'Inventory.RevealableCards.revealAll.cta',
            defaultMessage: 'Yes, reveal all',
          })}
        />
      </Actions>
      <Vertical gap={3}>
        {matchingRevealableCards.length === 0 ? (
          <Empty
            title={formatMessage(inventoryMessages.noRevealableCards)}
            description={
              <Horizontal>
                {results?.query
                  ? formatMessage(inventoryMessages.noRevealableMatchingCards, {
                      query: results?.query,
                    })
                  : ''}
                <Button
                  to={generateSportContextPath(ANY_SPORT_MARKET_INSTANT_BUY)}
                  size="medium"
                  color="quaternary"
                >
                  <FormattedMessage {...glossary.goToMarket} />
                </Button>
              </Horizontal>
            }
          />
        ) : (
          <Grid>
            {matchingRevealableCards.map(card => (
              <ItemButton
                key={card.slug}
                onClick={() =>
                  onSelect(cards.findIndex(c => c.slug === card.slug))
                }
              >
                <FlexCard card={card} />
              </ItemButton>
            ))}
          </Grid>
        )}
        {notYetRevealableCards.length > 0 && (
          <>
            <SectionSeparator>
              <FontAwesomeIcon icon={faHourglass} />
              <FormattedMessage
                id="Inventory.RevealableCards.delayed"
                defaultMessage="Delayed"
              />
            </SectionSeparator>
            <Info>
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>
                <FormattedMessage
                  id="Inventory.RevealableCards.delayedInfo"
                  defaultMessage="<b>These cards are soon to be opened!</b>{br}In the meantime, you can still play, trade, or sell them.{br}<a>More info</a>"
                  values={{
                    b: Bold,
                    a: s => (
                      <a href="#FIXME" target="_blank">
                        {s}
                      </a>
                    ),
                    br: <br />,
                  }}
                />
              </span>
            </Info>
            <Grid>
              {notYetRevealableCards.map(card => (
                <ItemButton
                  key={card.slug}
                  onClick={() =>
                    onSelect(cards.findIndex(c => c.slug === card.slug))
                  }
                  style={{ opacity: 0.5 }}
                >
                  <FlexCard card={card} />
                </ItemButton>
              ))}
            </Grid>
          </>
        )}
      </Vertical>
    </>
  );
};

type Props = {
  type: Rarity;
};

export const InventoryRevealableCardsTypeSearchPage = ({ type }: Props) => {
  const { generateSportContextPath, sport = Sport.FOOTBALL } =
    useSportContext();
  const { setCurrentIndex, blueprints, setShowSummary, currentUser, loading } =
    useInventoryRevealableCardsTypeLayoutData();
  const navigate = useNavigate();

  const onSelect = useCallback(
    (index: number) => {
      setCurrentIndex(index);
      navigate(
        generateSportContextPath(
          ANY_SPORT_PLAY_INVENTORY_REVEALABLE_CARDS_TYPE,
          { params: { type } }
        )
      );
    },
    [generateSportContextPath, navigate, setCurrentIndex, type]
  );

  return (
    <InstantCardSearch
      indexes={['Cards New']}
      analyticsTags={['Inventory']}
      defaultHitsPerPage={100}
      sport={sport}
      defaultFilters={[
        `user.id:${idFromObject(currentUser?.id || 'none')}`,
        joinFiltersWithOr(blueprints.map(c => `objectID:${c.slug}`)),
      ]}
    >
      {loading ? (
        <LoadingIndicator fullHeight />
      ) : (
        <RevealableCardsGrid
          cards={blueprints}
          onSelect={onSelect}
          showRevealed={() => setShowSummary(true)}
        />
      )}
    </InstantCardSearch>
  );
};
